/**
 * Copyright Higharc Inc. 2019
 */
import * as THREE from "three";
import type {
  ScreenSpaceViewNormalDefines,
  ScreenSpaceViewNormalUniforms,
  ScreenSpaceViewPositionDefines,
  ScreenSpaceViewPositionUniforms,
} from "./screenSpaceHelpersGLSL";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

export interface LineworkShaderDefines extends ScreenSpaceViewPositionDefines, ScreenSpaceViewNormalDefines {
  LINE_THICKNESS: number; // eslint-disable-line @typescript-eslint/naming-convention
}

export interface LineworkShaderUniforms extends ScreenSpaceViewPositionUniforms, ScreenSpaceViewNormalUniforms {
  renderInvSize: THREE.IUniform;
  depthMapPixelOffset: THREE.IUniform;
  depthMapInvSize: THREE.IUniform;
  normalDotProductThreshold: THREE.IUniform;
  isCompositing: THREE.IUniform;
  tCompositeSrc: THREE.IUniform;
  depthThreshold: THREE.IUniform;
  tMaterialIdBuffer: THREE.IUniform;
  transparent: THREE.IUniform;
}

interface LineworkShaderType {
  defines: LineworkShaderDefines;
  uniforms: LineworkShaderUniforms;
  vertexShader: string;
  fragmentShader: string;
}

export const LineworkShader: LineworkShaderType = {
  defines: {
    ...screenSpaceViewPositionDefineDefaults,
    ...screenSpaceViewNormalDefineDefaults,
    LINE_THICKNESS: 1,
  },

  uniforms: {
    ...screenSpaceViewPositionUniformDefaults,
    ...screenSpaceViewNormalUniformDefaults,

    renderInvSize: { value: new THREE.Vector2() },

    // Depth/normal maps may include padding so take that into account.
    // Note that the shader easily runs into precision issues unless the pixels in the depth map and rendered image are precisely aligned.
    depthMapPixelOffset: { value: new THREE.Vector2() },
    depthMapInvSize: { value: new THREE.Vector2() },

    isCompositing: { value: false },
    tCompositeSrc: { value: null },

    normalDotProductThreshold: { value: 0.6 },
    depthThreshold: { value: 1 },

    tMaterialIdBuffer: { value: null },

    transparent: { value: false },
  },

  vertexShader: UVVertexShader,

  fragmentShader: `#include <common>
#include <packing>

${screenSpaceViewNormalGLSL}
${screenSpaceViewPositionGLSL}

varying vec2 vUv;

uniform vec2 renderInvSize;
uniform vec2 depthMapPixelOffset;
uniform vec2 depthMapInvSize;

// The linework rendering can be composited on top of another texture.
uniform bool isCompositing;
uniform sampler2D tCompositeSrc;
uniform sampler2D tMaterialIdBuffer;

uniform bool transparent;

uniform float normalDotProductThreshold;

uniform float depthThreshold;

// Predict a neighboring pixel's view z value based on this pixel's normal.
float predictNeighborViewZ( const in vec2 screenPosition, const in float depth, const in float viewZ, const vec3 viewNormal, const vec2 neighborPixelOffset ) {
  #if PERSPECTIVE_CAMERA == 1
  // Get the view space ray corresponding to one of the neighboring pixels.
  vec2 neighborScreenPosition = screenPosition + renderInvSize * neighborPixelOffset;
  vec4 rayClip = vec4(neighborScreenPosition * 2.0 - 1.0, -1.0, 1.0);
  vec3 rayViewDir = normalize(vec3((cameraInverseProjectionMatrix * rayClip).xy, -1.0));
  // Intersect the ray with the view space plane defined by the view space position of the center pixel and the normal.
  float planeDistFromOrigin = dot(viewNormal, getViewPosition(screenPosition, depth, viewZ));
  float t = planeDistFromOrigin / dot(viewNormal, rayViewDir);
  return (t * rayViewDir).z;
  #else
  // Take just the scale factor from the inverse projection matrix and scale the 1 pixel step from screen space to view space.
  vec2 viewStep = mat2(cameraInverseProjectionMatrix) * renderInvSize * neighborPixelOffset * 2.0;
  if (neighborPixelOffset.x == 0.0) {
    return viewZ - viewNormal.y / viewNormal.z * viewStep.y;
  } else {
    return viewZ - viewNormal.x / viewNormal.z * viewStep.x;
  }
  #endif
}

int getMaterialId(vec2 uv) {
  vec4 rgba = texture2D(tMaterialIdBuffer, uv);
  return int(rgba.r * 255.0 + rgba.g * 255.0 * 256.0 + 0.5);
}

void main() {

  vec2 centerDepthUv = (gl_FragCoord.xy + depthMapPixelOffset) * depthMapInvSize;
  vec3 normalCenter = getViewNormal( centerDepthUv );
  float depthCenter = GET_DEPTH(tDepth, centerDepthUv);
  float viewZCenter = getViewZ( depthCenter );

  vec2 centerIdUv = gl_FragCoord.xy * renderInvSize;
  int idCenter = getMaterialId(centerIdUv);

  float viewZThreshold = depthThreshold;

  // Avoid artifacts from depth quantization errors. We're assuming the worst case of a 16-bit int depth buffer.
  const float depthEpsilon = 1.1 / (pow(2.0, 16.0) - 1.0);
  float minViewZThreshold = getViewZ(depthCenter - depthEpsilon) - viewZCenter;

  // Try to take into account errors caused by normal quantization, which is an issue especially for surfaces nearly
  // perpendicular to the camera. This is just an ad-hoc adjustment. If we wanted a better adjustment we should do
  // a thorough analysis on how normal precision affects the precision of predictNeighborViewZ().
  minViewZThreshold *= max( normalCenter.x * 50.0 - 42.0, 1.0 ) * max( normalCenter.y * 50.0 - 42.0, 1.0 );

  viewZThreshold = max( viewZThreshold, minViewZThreshold );

  bool hasEdge = false;
  float offsetSign = 1.0;
  float offsetAmplitude = 1.0;
  for ( int i = 0; i < LINE_THICKNESS; ++i ) {
    vec3 normalNeighborH = getViewNormal( centerDepthUv + depthMapInvSize * vec2( offsetSign * offsetAmplitude, 0 ) );
    vec3 normalNeighborV = getViewNormal( centerDepthUv + depthMapInvSize * vec2( 0, offsetSign * offsetAmplitude ) );

    hasEdge = hasEdge || (
      dot( normalCenter, normalNeighborH ) < normalDotProductThreshold || 
      dot( normalCenter, normalNeighborV ) < normalDotProductThreshold
    );

    float depthNeighborH = GET_DEPTH(tDepth, centerDepthUv + depthMapInvSize * vec2(offsetSign * offsetAmplitude, 0));
    float depthNeighborV = GET_DEPTH(tDepth, centerDepthUv + depthMapInvSize * vec2(0, offsetSign * offsetAmplitude));
    if (depthCenter < 1.0) {
      float viewZNeighborH = getViewZ( depthNeighborH );
      float viewZNeighborV = getViewZ( depthNeighborV );

      hasEdge = hasEdge ||
        abs( predictNeighborViewZ( vUv, depthCenter, viewZCenter, normalCenter, vec2( offsetSign * offsetAmplitude, 0 ) ) - viewZNeighborH ) > viewZThreshold ||
        abs( predictNeighborViewZ( vUv, depthCenter, viewZCenter, normalCenter, vec2( 0, offsetSign * offsetAmplitude ) ) - viewZNeighborV ) > viewZThreshold;
    } else {
      hasEdge = hasEdge || depthNeighborH != depthCenter || depthNeighborV != depthCenter;
    }

    int idNeighborH = getMaterialId(centerIdUv + renderInvSize * vec2(offsetSign * offsetAmplitude, 0));
    int idNeighborV = getMaterialId(centerIdUv + renderInvSize * vec2(0, offsetSign * offsetAmplitude));
    hasEdge = hasEdge || idNeighborH != idCenter || idNeighborV != idCenter;

    offsetSign = -offsetSign;
    offsetAmplitude += (offsetSign > 0.0) ? 1.0 : 0.0;
  }

  gl_FragColor = vec4(vec3(!hasEdge), transparent ? float(hasEdge) : 1.0);
  
  vec4 compositeSrc = texture2D(tCompositeSrc, vUv);

  if (isCompositing) {
    if (transparent) {
      if (!hasEdge) {
        gl_FragColor = compositeSrc;
      }
    } else {
      gl_FragColor.rgb *= compositeSrc.rgb;
    }
  }
}`,
};
